import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, RotateCcw, Volume2, VolumeX } from 'lucide-react';

/**
 * Sets × Reps × Work/Rest Interval Timer.
 * State machine per rep: WORK → REP_REST → (next rep) OR SET_REST → (next set) → COMPLETE
 * Audio: work-start beep (high), rest beep (low), final bell (triple chime) via Web Audio API.
 */
export default function DrillTimer({ timer, onComplete }) {
  const {
    sets = 3,
    reps_per_set = 10,
    work_seconds = 30,
    rest_between_reps_seconds = 0,
    rest_between_sets_seconds = 60,
  } = timer || {};

  const [running, setRunning] = useState(false);
  const [phase, setPhase] = useState('idle'); // 'idle' | 'work' | 'rep-rest' | 'set-rest' | 'complete'
  const [setIdx, setSetIdx] = useState(1);
  const [repIdx, setRepIdx] = useState(1);
  const [remaining, setRemaining] = useState(work_seconds);
  const [muted, setMuted] = useState(false);

  const audioCtxRef = useRef(null);
  const intervalRef = useRef(null);

  const ensureAudio = useCallback(() => {
    if (muted) return null;
    if (!audioCtxRef.current) {
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        audioCtxRef.current = new Ctx();
      } catch { return null; }
    }
    if (audioCtxRef.current.state === 'suspended') audioCtxRef.current.resume();
    return audioCtxRef.current;
  }, [muted]);

  const playBeep = useCallback((freq, duration = 0.15, type = 'sine', when = 0) => {
    const ctx = ensureAudio();
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.0001, ctx.currentTime + when);
    gain.gain.exponentialRampToValueAtTime(0.4, ctx.currentTime + when + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + when + duration);
    osc.connect(gain).connect(ctx.destination);
    osc.start(ctx.currentTime + when);
    osc.stop(ctx.currentTime + when + duration + 0.02);
  }, [ensureAudio]);

  const playWorkBeep = useCallback(() => playBeep(880, 0.2, 'square'), [playBeep]);
  const playRestBeep = useCallback(() => playBeep(330, 0.3, 'sine'), [playBeep]);
  const playBell = useCallback(() => {
    playBeep(988, 0.25, 'triangle', 0);
    playBeep(1319, 0.25, 'triangle', 0.2);
    playBeep(1568, 0.5, 'triangle', 0.4);
  }, [playBeep]);

  // Core tick
  useEffect(() => {
    if (!running || phase === 'complete' || phase === 'idle') return;
    intervalRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r > 1) return r - 1;
        // Phase transition
        advancePhase();
        return 0;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, phase]);

  const advancePhase = () => {
    if (phase === 'work') {
      // Reps within a set
      if (repIdx < reps_per_set) {
        if (rest_between_reps_seconds > 0) {
          setPhase('rep-rest');
          setRemaining(rest_between_reps_seconds);
          playRestBeep();
        } else {
          setRepIdx(r => r + 1);
          setRemaining(work_seconds);
          playWorkBeep();
        }
      } else if (setIdx < sets) {
        setPhase('set-rest');
        setRemaining(rest_between_sets_seconds);
        playRestBeep();
      } else {
        setPhase('complete');
        setRunning(false);
        playBell();
        if (onComplete) onComplete();
      }
    } else if (phase === 'rep-rest') {
      setRepIdx(r => r + 1);
      setPhase('work');
      setRemaining(work_seconds);
      playWorkBeep();
    } else if (phase === 'set-rest') {
      setSetIdx(s => s + 1);
      setRepIdx(1);
      setPhase('work');
      setRemaining(work_seconds);
      playWorkBeep();
    }
  };

  const start = () => {
    ensureAudio(); // unlock audio on user gesture
    if (phase === 'idle' || phase === 'complete') {
      setSetIdx(1);
      setRepIdx(1);
      setPhase('work');
      setRemaining(work_seconds);
      playWorkBeep();
    }
    setRunning(true);
  };

  const pause = () => setRunning(false);

  const reset = () => {
    setRunning(false);
    setPhase('idle');
    setSetIdx(1);
    setRepIdx(1);
    setRemaining(work_seconds);
  };

  const phaseLabel = {
    idle: 'Ready',
    work: 'Work',
    'rep-rest': 'Rest',
    'set-rest': 'Set Rest',
    complete: 'Complete!',
  }[phase];

  const phaseColor = {
    idle: '#2563EB',
    work: '#10B981',
    'rep-rest': '#FF9100',
    'set-rest': '#FF9100',
    complete: '#FFD700',
  }[phase];

  const totalDuration = (
    phase === 'work' ? work_seconds :
    phase === 'rep-rest' ? rest_between_reps_seconds :
    phase === 'set-rest' ? rest_between_sets_seconds :
    work_seconds
  );
  const progress = totalDuration > 0 ? ((totalDuration - remaining) / totalDuration) * 100 : 0;

  return (
    <div
      className="bg-[#0A2242] rounded-2xl p-5 text-white shadow-lg"
      data-testid="drill-timer"
    >
      {/* Top row — set / rep indicator + mute */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/60 font-bold">Interval Timer</div>
        </div>
        <button
          onClick={() => setMuted(m => !m)}
          className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
          aria-label={muted ? 'Unmute' : 'Mute'}
          data-testid="timer-mute-btn"
        >
          {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
        </button>
      </div>

      {/* Set / Rep Counters */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-white/5 border border-white/10 rounded-lg p-2 text-center">
          <p className="text-[9px] uppercase tracking-widest text-white/50 font-bold">Set</p>
          <p className="font-['Barlow_Condensed'] text-2xl font-black">{setIdx}<span className="text-white/40 text-lg"> / {sets}</span></p>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-lg p-2 text-center">
          <p className="text-[9px] uppercase tracking-widest text-white/50 font-bold">Rep</p>
          <p className="font-['Barlow_Condensed'] text-2xl font-black">{repIdx}<span className="text-white/40 text-lg"> / {reps_per_set}</span></p>
        </div>
      </div>

      {/* Main Timer Display */}
      <div className="text-center mb-4">
        <AnimatePresence mode="wait">
          <motion.div
            key={phase}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ opacity: 0 }}
            className="font-['Barlow_Condensed'] text-[11px] uppercase tracking-[0.3em] font-bold mb-1"
            style={{ color: phaseColor }}
          >
            {phaseLabel}
          </motion.div>
        </AnimatePresence>
        <p
          className="font-['Barlow_Condensed'] text-7xl font-black leading-none tabular-nums"
          style={{ color: phase === 'complete' ? phaseColor : '#ffffff' }}
          data-testid="timer-display"
        >
          {Math.max(0, Math.floor(remaining / 60))}:{(remaining % 60).toString().padStart(2, '0')}
        </p>
      </div>

      {/* Progress bar */}
      <div className="w-full h-1.5 bg-white/10 rounded-full mb-4 overflow-hidden">
        <motion.div
          className="h-full rounded-full"
          style={{ backgroundColor: phaseColor }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.3, ease: 'linear' }}
        />
      </div>

      {/* Controls */}
      <div className="flex gap-2">
        {!running ? (
          <button
            onClick={start}
            className="flex-1 bg-[#2563EB] hover:bg-[#3B82F6] rounded-lg py-3 font-bold text-sm uppercase tracking-wide flex items-center justify-center gap-2"
            data-testid="timer-start-btn"
          >
            <Play size={16} fill="white" />
            {phase === 'complete' ? 'Restart' : phase === 'idle' ? 'Start' : 'Resume'}
          </button>
        ) : (
          <button
            onClick={pause}
            className="flex-1 bg-[#FF9100] hover:bg-[#FFA733] rounded-lg py-3 font-bold text-sm uppercase tracking-wide flex items-center justify-center gap-2"
            data-testid="timer-pause-btn"
          >
            <Pause size={16} fill="white" />
            Pause
          </button>
        )}
        <button
          onClick={reset}
          className="w-12 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center"
          aria-label="Reset"
          data-testid="timer-reset-btn"
        >
          <RotateCcw size={16} />
        </button>
      </div>

      {/* Footer — total work summary */}
      <p className="text-[9px] text-white/40 uppercase tracking-[0.2em] text-center mt-3">
        {sets} sets × {reps_per_set} reps · {work_seconds}s work
        {rest_between_reps_seconds > 0 && ` · ${rest_between_reps_seconds}s rep rest`}
        {` · ${rest_between_sets_seconds}s set rest`}
      </p>
    </div>
  );
}
